import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/services/api';

export interface SettingImage {
  id: number;
  settingKey: string;
  filePath: string;
  isActive: boolean;
  createdAt: string;
}

export const useSettingsStore = defineStore('settings', () => {
  // State
  const settings = ref<Record<string, string>>({});
  const logoImages = ref<SettingImage[]>([]);
  const faviconImages = ref<SettingImage[]>([]);
  const isLoaded = ref(false);
  const isLoading = ref(false);

  // API Base URL (uploads için tam URL)
  const API_BASE_URL = (process.env.VUE_APP_API_URL || 'http://localhost:7285/api').replace(/\/api\/?$/, '');

  // Path'i tam URL'e çevir
  function getFullUrl(path: string | null | undefined): string | null {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) return path;
    return `${API_BASE_URL}/${path.replace(/^\//, '')}`;
  }

  // Getters
  const customLogo = computed(() => settings.value['site_logo'] || null);
  const customFavicon = computed(() => settings.value['site_favicon'] || null);
  const hasCustomLogo = computed(() => !!customLogo.value);
  const hasCustomFavicon = computed(() => !!customFavicon.value);
  const logoUrl = computed(() => getFullUrl(customLogo.value));
  const faviconUrl = computed(() => getFullUrl(customFavicon.value));

  // Tüm ayarları API'den çek
  async function fetchSettings() {
    if (isLoading.value) return;
    isLoading.value = true;
    try {
      const response = await api().get('/Settings');
      const data = response.data;

      if (Array.isArray(data)) {
        const map: Record<string, string> = {};
        data.forEach((item: any) => {
          const key = item.key || item.Key || '';
          const value = item.value || item.Value || '';
          if (key) map[key] = value;
        });
        settings.value = map;
      } else if (typeof data === 'object' && data !== null) {
        settings.value = data;
      }

      isLoaded.value = true;

      // Favicon'u DOM'a uygula
      if (customFavicon.value) {
        updateFaviconInDOM(getFullUrl(customFavicon.value)!);
      }
    } catch (error) {
      console.error('Settings fetch error:', error);
    } finally {
      isLoading.value = false;
    }
  }

  // Belirli key için tüm görselleri çek (galeri)
  async function fetchImages(key: string) {
    try {
      const response = await api().get(`/Settings/images/${key}`);
      const data = response.data;
      const images: SettingImage[] = (Array.isArray(data) ? data : (data?.images || data?.data || []))
        .map((item: any) => ({
          id: item.id || item.Id,
          settingKey: item.settingKey || item.SettingKey || key,
          filePath: item.filePath || item.FilePath || '',
          isActive: item.isActive ?? item.IsActive ?? false,
          createdAt: item.createdAt || item.CreatedAt || '',
        }));

      if (key === 'site_logo') {
        logoImages.value = images;
      } else if (key === 'site_favicon') {
        faviconImages.value = images;
      }
      return images;
    } catch (error) {
      console.error(`Fetch images error (${key}):`, error);
      return [];
    }
  }

  // Yeni dosya yükle (galeriye ekle + aktif yap)
  async function uploadImage(key: string, file: File): Promise<SettingImage | null> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('key', key);

    const response = await api().post('/Settings/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const data = response.data;
    const filePath = data?.filePath || data?.FilePath || data?.path || data?.Path || '';

    // Store'u güncelle
    settings.value[key] = filePath;

    // Galeriyi yenile
    await fetchImages(key);

    if (key === 'site_favicon') {
      updateFaviconInDOM(getFullUrl(filePath)!);
    }

    return data;
  }

  // Mevcut görseli aktif yap (galeri geçişi)
  async function activateImage(key: string, imageId: number) {
    await api().put(`/Settings/images/${imageId}/activate`);
    
    // Galeriyi yenile
    const images = await fetchImages(key);
    
    // Aktif olan görselin path'ini settings'e yaz
    const activeImage = images.find(img => img.isActive);
    if (activeImage) {
      settings.value[key] = activeImage.filePath;
      
      if (key === 'site_favicon') {
        updateFaviconInDOM(getFullUrl(activeImage.filePath)!);
      }
    }
  }

  // Görseli galeriden sil
  async function deleteImage(key: string, imageId: number) {
    await api().delete(`/Settings/images/${imageId}`);
    await fetchImages(key);
  }

  // Ayarı sıfırla (aktif logoyu kaldır, varsayılana dön)
  async function resetSetting(key: string) {
    try {
      await api().put(`/Settings/${key}`, { value: '' });
      settings.value[key] = '';
      
      // Galeridekilerin isActive'ini false yap (refresh)
      await fetchImages(key);

      if (key === 'site_favicon') {
        resetFaviconInDOM();
      }
    } catch (error) {
      console.error('Reset setting error:', error);
      throw error;
    }
  }

  function initFavicon() {
    const url = getFullUrl(customFavicon.value);
    if (url) {
      updateFaviconInDOM(url);
    }
  }

  return {
    settings,
    logoImages,
    faviconImages,
    isLoaded,
    isLoading,
    customLogo,
    customFavicon,
    hasCustomLogo,
    hasCustomFavicon,
    logoUrl,
    faviconUrl,
    getFullUrl,
    fetchSettings,
    fetchImages,
    uploadImage,
    activateImage,
    deleteImage,
    resetSetting,
    initFavicon,
  };
});

// DOM Helpers
function updateFaviconInDOM(url: string) {
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

function resetFaviconInDOM() {
  const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (link) {
    link.href = '/favicon.ico';
  }
}
